/**
 * Freeze V2 — Validation Tab
 *
 * Validate selected fertilizations with pre-submit checks
 * matching v1 behavior: QC field validation, storage location checks,
 * math validation, and "last validated by" display.
 */
import { useCallback, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { addToast } from '@/app/slices/toast';
import { SearchableSelect } from '@/components/common/searchable-select';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { Button } from '@/components/ui/button';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
} from '@/services/freeze';
import { useFreezeValidationReport } from '@/services/reports';
import { getValidateFreezeSiteId } from '@/utils/freezeSites';

import { useFreezeSelection } from '../../contexts';
import { useValidateFreezeMutation } from '../../services/freezeApi';
import { DATE_PICKER_MATCH, MOBILE_BREAKPOINT } from '../../utils/constants';
import { parseWallClock, toWallClockISO } from '@/utils/wallClock';

type QCMissingField = { embryoName: string; missingFields: string[] };

type ValidationTabProps = {
  fertilizations: FertilizationListFreezeResponse[];
  embryosByFert: Map<string, FertAndEmbryosResponse>;
};

export function ValidationTab({
  fertilizations,
  embryosByFert,
}: ValidationTabProps) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const { fertilizationIds, liveEmbryoIds } = useFreezeSelection();

  const [params] = useSearchParams();
  const fertilizationDate = params.get('date') || '';
  const fertilizationSiteId = params.get('sId') || '';

  const [validatorId, setValidatorId] = useState('');
  const [validationDate, setValidationDate] = useState<Date | null>(null);
  const [showQCDialog, setShowQCDialog] = useState(false);
  const [qcMissingFields, setQcMissingFields] = useState<QCMissingField[]>([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [errorTitle, setErrorTitle] = useState('');

  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });

  const techOptions = useMemo(
    () =>
      (techsData ?? []).map((tech) => ({
        label: tech.name ?? '',
        value: tech.id ?? '',
      })),
    [techsData],
  );

  const { mutateAsync: validateFreeze, isPending: isValidating } =
    useValidateFreezeMutation();
  const [generateFreezeValidationReport, { isPending: isGenReport }] =
    useFreezeValidationReport();

  const lastValidation = useMemo(() => {
    const sorted = [...fertilizations]
      .filter((f) => f.validationDate)
      .sort(
        (a, b) =>
          new Date(b.validationDate as string).getTime() -
          new Date(a.validationDate as string).getTime(),
      );
    if (sorted.length === 0) return null;
    const validId = sorted[0].validatorId;
    const validDate = sorted[0].validationDate;
    return {
      validatorId: validId,
      validationDate: parseWallClock(validDate),
    };
  }, [fertilizations]);

  const lastValidatorName = useMemo(() => {
    if (!lastValidation?.validatorId || techOptions.length === 0) return null;
    return (
      techOptions.find((t) => t.value === lastValidation.validatorId)?.label ??
      null
    );
  }, [lastValidation, techOptions]);

  const checkQCFields = useCallback((): QCMissingField[] => {
    const selectedFerts = fertilizations.filter(
      (f) => f.fertId && fertilizationIds.includes(f.fertId),
    );
    const missing: QCMissingField[] = [];

    for (const fert of selectedFerts) {
      const embryoData = embryosByFert.get(fert.fertId ?? '');
      if (!embryoData) continue;

      for (const emb of embryoData.liveEmbryos) {
        const freezeMethod = (
          emb.freezeMethodName ??
          fert.freezeMethod ??
          ''
        ).toLowerCase();
        const isExport = fert.export?.toString().toLowerCase() === 'yes';
        const fields: string[] = [];

        if (
          freezeMethod.includes('1.5m eg') &&
          freezeMethod.includes('suc') &&
          !freezeMethod.includes('vit')
        ) {
          if (!emb.qcEg10) fields.push('EG Lot Number');
          if (!emb.qcEg10Date) fields.push('EG Expiration Date');
          if (!emb.qcehmDate) fields.push('EHM Expiration Date');
          if (!emb.qcSucroseDate) fields.push('Sucrose Expiration Date');
          if (!emb.qcStrawLotNumber) fields.push('Straw Lot #');
          if (!emb.qc12WellPlates?.trim()) fields.push('12 Well Lot #');
        } else if (freezeMethod.includes('vit')) {
          if (!emb.qcehmDate) fields.push('EHM Expiration Date');
          if (!emb.equilibrationSolutionDate) fields.push('ES Expiration Date');
          if (!emb.vitrificationSolutionDate) fields.push('VS Expiration Date');
          if (!emb.qcVitStrawLotNumber) fields.push('Vit Straw Lot #');
        }

        if (isExport) {
          if (!emb.qcEg10) fields.push('EG Lot Number');
          if (!emb.qcEg10Date) fields.push('EG Expiration Date');
          if (!emb.qcehmDate) fields.push('EHM Expiration Date');
          if (!emb.qcSucroseDate) fields.push('Sucrose Expiration Date');
          if (!emb.qcSterileStrawLotNumber) fields.push('Sterile Straw Lot #');
          if (!freezeMethod.includes('vit') && !emb.qc12WellPlates?.trim())
            fields.push('12 Well Lot #');
        }

        if (fields.length > 0) {
          missing.push({
            embryoName: emb.name ?? emb.id ?? 'Unknown',
            missingFields: [...new Set(fields)],
          });
        }
      }
    }
    return missing;
  }, [fertilizations, embryosByFert, fertilizationIds]);

  const checkConditions = useCallback((): { ok: boolean } => {
    const selectedFerts = fertilizations.filter(
      (f) => f.fertId && fertilizationIds.includes(f.fertId),
    );
    const msgs: string[] = [];
    const titleParts: string[] = [];
    let mathOk = true;

    for (const fert of selectedFerts) {
      const embryoData = embryosByFert.get(fert.fertId ?? '');
      if (!embryoData) continue;
      const plate = fert.plateNumber;
      const donor = fert.donorName ?? '';
      const sire = fert.sireName ?? '';
      const label = `Plate ${plate} - ${donor} x ${sire}`;

      let hasStorageMissing = false;
      for (const emb of embryoData.liveEmbryos) {
        if (!liveEmbryoIds.includes(emb.id ?? '')) continue;
        if (!emb.storageLocationId && !hasStorageMissing) {
          msgs.push(`${label}: Missing storage location`);
          hasStorageMissing = true;
        }
        if (!emb.grader7Name) {
          msgs.push(`${label}: Missing grader`);
        }
        if (!emb.freezeTechName) {
          msgs.push(`${label}: Missing freeze tech`);
        }
      }

      const data = fert;
      if (data.intEmbs === 0 && data.embs === 0) {
        const held = data.heldAtTogToFreeze ?? 0;
        const totalDiscards =
          (data.degenerates ?? 0) +
          (data.discarded ?? 0) +
          (data.discardedGrade2 ?? 0) +
          (data.discardedGrade1 ?? 0);
        if (!(held === totalDiscards && held > 0)) {
          // orange — not strictly red, but not green either
        }
      } else {
        const total =
          (data.embs ?? 0) +
          (data.intEmbs ?? 0) +
          (data.degenerates ?? 0) +
          (data.discarded ?? 0) +
          (data.discardedGrade2 ?? 0) +
          (data.discardedGrade1 ?? 0);
        const held = data.heldAtTogToFreeze ?? 0;
        const isTOGSite =
          fertilizationSiteId === '1' ||
          fertilizationSiteId === '2' ||
          fertilizationSiteId === '3';

        if (isTOGSite) {
          if (total < held) {
            msgs.push(`${label}: Math validation failed`);
            mathOk = false;
          }
        } else {
          if (total !== held) {
            msgs.push(`${label}: Math validation failed`);
            mathOk = false;
          }
        }
      }
    }

    if (msgs.length > 0) {
      if (!mathOk) titleParts.push('Validation Failure');
      if (msgs.some((m) => m.includes('Missing storage')))
        titleParts.push('Missing Storage Locations');
      if (
        msgs.some(
          (m) =>
            m.includes('Missing grader') || m.includes('Missing freeze tech'),
        )
      )
        titleParts.push('Missing Grader or Freeze Tech');

      setErrorTitle(titleParts.join(' & ') || 'Validation Issues');
      setErrorMessages([...new Set(msgs)]);
      setShowErrorDialog(true);
      return { ok: false };
    }
    return { ok: true };
  }, [
    fertilizations,
    embryosByFert,
    fertilizationIds,
    liveEmbryoIds,
    fertilizationSiteId,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!validatorId || !validationDate) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Missing Fields',
          detail: 'Validator and date are required.',
          life: 3000,
        }),
      );
      return;
    }
    if (fertilizationIds.length === 0) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'No Selection',
          detail: 'Select fertilizations to validate.',
          life: 3000,
        }),
      );
      return;
    }

    const qcMissing = checkQCFields();
    if (qcMissing.length > 0) {
      setQcMissingFields(qcMissing);
      setShowQCDialog(true);
      return;
    }

    const { ok } = checkConditions();
    if (!ok) return;

    try {
      const adjusted = new Date(validationDate);
      const now = new Date();
      adjusted.setHours(
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds(),
      );

      await validateFreeze({
        fertilizationIds,
        fertilizationDate,
        fertilizationSiteId: getValidateFreezeSiteId(fertilizationSiteId),
        validatorId,
        validationDate: toWallClockISO(adjusted),
      });

      dispatch(
        addToast({
          severity: 'success',
          summary: 'Validated',
          detail: `${fertilizationIds.length} fertilizations validated.`,
          life: 3000,
        }),
      );
    } catch {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Validation failed.',
          life: 3000,
        }),
      );
    }
  }, [
    validatorId,
    validationDate,
    fertilizationIds,
    fertilizationDate,
    fertilizationSiteId,
    validateFreeze,
    dispatch,
    checkQCFields,
    checkConditions,
  ]);

  const handleReport = useCallback(async () => {
    if (fertilizationIds.length === 0) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'No Selection',
          detail: 'Select fertilizations first.',
          life: 3000,
        }),
      );
      return;
    }
    await generateFreezeValidationReport({ fertilizationIds });
  }, [fertilizationIds, generateFreezeValidationReport, dispatch]);

  const gridClasses = isMobile
    ? 'flex flex-col gap-3'
    : 'flex flex-row items-end gap-3';

  return (
    <div className="flex flex-col gap-3">
      <div className={gridClasses}>
        <Field className="w-60">
          <FieldLabel>Validator</FieldLabel>
          <SearchableSelect
            className="h-8 w-full rounded-lg px-2.5 py-1 text-sm"
            options={techOptions}
            value={validatorId}
            onChange={setValidatorId}
            placeholder="Select validator"
            emptyMessage="No technicians found"
          />
        </Field>

        <div className="flex w-60 flex-col">
          <DatePickerInput
            label="Date Validated"
            onChange={setValidationDate}
            className={DATE_PICKER_MATCH}
          />
        </div>

        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={
            isValidating ||
            !validatorId ||
            !validationDate ||
            fertilizationIds.length === 0
          }
        >
          {isValidating ? (
            <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
          ) : (
            <i className="pi pi-check mr-1.5 text-xs" />
          )}
          {isValidating
            ? 'Validating…'
            : `Validate (${fertilizationIds.length})`}
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={handleReport}
          disabled={isGenReport || fertilizationIds.length === 0}
        >
          {isGenReport ? (
            <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
          ) : (
            <i className="pi pi-file-pdf mr-1.5 text-xs" />
          )}
          {isGenReport ? 'Generating…' : 'Validation Report'}
        </Button>
      </div>

      {lastValidatorName && lastValidation?.validationDate && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Last validated by <strong>{lastValidatorName}</strong> on{' '}
          {lastValidation.validationDate.toLocaleDateString()}
        </p>
      )}

      {fertilizationIds.length === 0 && (
        <p className="text-xs text-gray-400">
          Select fertilizations in the grid, then validate here.
        </p>
      )}

      <Dialog open={showQCDialog} onOpenChange={setShowQCDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              QC Fields Missing From Selected Live Embryos
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 text-sm">
            <p className="font-semibold">Reminder</p>
            <p>
              For matings with Freeze Method of 1.5M EG w/ sucrose: Include EG
              Lot Number, EG Expiration Date, EHM Expiration Date, Sucrose
              Expiration Date, Straw Lot #, &amp; 12 Well Lot #
            </p>
            <p>
              For matings with Freeze Method of Vit: Include EHM Expiration
              Date, VS Expiration Date, ES Expiration Date, Vit Straw Lot #
            </p>
            <p>
              For matings that have export column as YES: Include EG Lot Number,
              EG Expiration Date, EHM Expiration Date, Sucrose Expiration Date,
              Sterile Straw Lot #, and 12 Well Lot #
            </p>
            {qcMissingFields.length > 0 && (
              <div className="mt-2">
                <strong>Missing Fields:</strong>
                <ul className="mt-1 ml-4 list-disc">
                  {qcMissingFields.map((emb, idx) => (
                    <li key={idx}>
                      <strong>{emb.embryoName}:</strong>{' '}
                      {emb.missingFields.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{errorTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 text-sm">
            {errorMessages.map((msg, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <i className="pi pi-exclamation-triangle mt-0.5 text-orange-500" />
                <span>{msg}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
